/**
 * SEO-friendly error handling composable
 * Provides graceful degradation while maintaining SEO benefits
 */

export interface SEOErrorOptions {
  fallbackTitle?: string
  fallbackDescription?: string
  statusCode?: number
  logError?: boolean
}

export const useSSRFallback = () => {
  /**
   * Handles API data fetching with SEO-friendly fallbacks
   */
  const fetchWithFallback = async <T>(
    key: string,
    fetcher: () => Promise<T>,
    options: SEOErrorOptions = {}
  ) => {
    const {
      fallbackTitle = '北美好医生 - 专业医疗服务平台',
      fallbackDescription = '连接优质医疗资源，服务华人健康需求',
      statusCode = 200,
      logError = true
    } = options

    try {
      // Use useAsyncData with proper error handling
      const { data, error, pending } = await useAsyncData(key, async () => {
        try {
          return await fetcher()
        } catch (fetchError) {
          if (logError) {
            console.warn(`[SSR Fallback] API fetch failed for ${key}:`, fetchError)
          }
          
          // Return null to trigger fallback
          return null
        }
      }, {
        server: true,
        client: true,
        default: () => null
      })

      // If data fetch failed but we're on server, set basic SEO
      if (error.value && process.server) {
        const { setSEOMeta } = useSEO()
        setSEOMeta({
          title: fallbackTitle,
          description: fallbackDescription,
          locale: 'zh-Hans'
        })
      }

      return { data, error, pending }
    } catch (composableError) {
      if (logError) {
        console.error(`[SSR Fallback] Composable error for ${key}:`, composableError)
      }

      // Create error that won't break SSR
      const safeError = ref(composableError)
      const safeData = ref(null)
      const safePending = ref(false)

      return {
        data: safeData,
        error: safeError,
        pending: safePending
      }
    }
  }

  /**
   * Sets fallback SEO meta when data is unavailable
   */
  const setFallbackSEO = (options: {
    title: string
    description: string
    keywords?: string[]
  }) => {
    try {
      const { setSEOMeta } = useSEO()
      setSEOMeta({
        title: options.title,
        description: options.description,
        keywords: options.keywords || ['北美好医生', '医疗服务', '华人医疗'],
        locale: 'zh-Hans'
      })
    } catch (error) {
      console.warn('[SSR Fallback] Failed to set fallback SEO:', error)
    }
  }

  /**
   * Graceful error boundary for components
   */
  const withErrorBoundary = <T>(
    computation: () => T,
    fallback: T,
    errorMessage = 'Component computation failed'
  ): T => {
    try {
      return computation()
    } catch (error) {
      console.warn(`[SSR Fallback] ${errorMessage}:`, error)
      return fallback
    }
  }

  return {
    fetchWithFallback,
    setFallbackSEO,
    withErrorBoundary
  }
}
